import { api } from "./client";
import type { ApiResponse, PaginatedResponse } from "./types";

export interface ProviderMakeGoodResult {
  status: string;
  sourceCampaignId: string;
  sourceHeadline: string;
  providerId: string;
  providerName: string;
  expectedRevision: number;
  replacementCampaignId: string | null;
  eligibleRenders: number;
  targetEligibleRenders: number;
  startsAt: string;
  expiresAt: string;
  requiresReview: boolean;
  chargingEnabled: boolean;
}

export function prepareProviderMakeGood(campaignId: string, input: {
  startsAt: string; expiresAt: string; apply?: boolean; expectedRevision?: number;
}) {
  return api.post<ApiResponse<ProviderMakeGoodResult>>(
    `/admin/providers/campaigns/${campaignId}/make-good`, input,
  );
}

// ─── Shapes ─────────────────────────────────────────────────────────
// Mirrors apps/api/src/modules/providers/providers.service.ts return maps.
// NOTE: the list endpoint puts `meta` as a SIBLING of `data` (not nested)
// because the controller calls sendSuccess(res, providers, 200, meta).

export interface ProviderCategoryRef {
  code: string;
  name: string;
  icon?: string | null;
}

export interface ProviderListItem {
  id: string;
  name: string;
  slug: string;
  category: ProviderCategoryRef;
  subcategory: { code: string; name: string } | null;
  description: string | null;
  city: string;
  state: string;
  vouchCount: number;
  avgRating: number;
  mentionCount: number;
  languages: string[];
  skills: string[];
  /** The business's own statement about its food: jain, no_onion_garlic, eggless, pure_veg_kitchen. */
  dietFacts: string[];
  originState: string | null;
  isVerified: boolean;
  addedBy?: { id: string; name: string; avatarUrl: string | null } | null;
  createdAt: string;
  /** A service the signed-in member said they need; these come first while browsing. */
  forYourNeed?: boolean;
  /** Free text per day, mon..sun. Null or a null day: not stated. */
  hours: Record<string, string | null> | null;
  /** Open at this moment on the business's clock; null: we cannot say. */
  openNow: boolean | null;
}

export interface ProviderVouch {
  id: string;
  rating: number;
  comment: string | null;
  occasion?: string | null;
  user: { id: string; name: string; avatarUrl: string | null };
  createdAt: string;
}

export interface ProviderReferenceItem {
  id: string;
  name: string;
  relationship: string;
  note: string | null;
  createdAt: string;
}

export interface ProviderVerificationBadge {
  type: ProviderVerificationType;
  verifiedAt: string | null;
}

export interface ProviderDetail extends Omit<ProviderListItem, never> {
  priceBand: string | null;
  serviceAreas: string[];
  phone: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  zipCode: string | null;
  /** The business's own photos. Never the name card below. */
  images: string[];
  /** The card WeDesi drew for a listing with no photo of its own; null when none. */
  nameCardUrl: string | null;
  /** Approved, non-expired verification types — public trust badges. */
  verifications: ProviderVerificationBadge[];
  /** Provider-supplied references (off-platform social proof). */
  references: ProviderReferenceItem[];
  recentVouches: ProviderVouch[];
  /** How many of the caller's own connections vouched; null signed out. */
  vouchesFromPeopleYouKnow?: number | null;
  /** The caller's part in running it: primary_owner, manager, or null. */
  viewerRole?: string | null;
  /** "Meet the owner": null unless the owner chose to be shown. */
  owner?: import('./business-owner').BusinessOwner | null;
  recentMentions: {
    id: string;
    question: { id: string; title: string };
    answeredBy: { id: string; name: string; avatarUrl: string | null };
    createdAt: string;
  }[];
}

export interface ProviderCategory {
  code: string;
  name: string;
  icon: string | null;
  sortOrder: number;
  /** Curated capability tags for this category (primes enroll + filter). */
  suggestedSkills: string[];
  subcategories: { code: string; name: string }[];
}

export interface ListProvidersParams {
  category?: string;
  subcategory?: string;
  city?: string;
  state?: string;
  /** Indian origin state — code ("GJ") or name ("Gujarat"); normalized server-side. */
  originState?: string;
  /** Verified providers only. */
  verified?: boolean;
  /** Comma-separated skills — matches providers offering ANY of them. */
  skills?: string;
  /** Comma-separated diet facts — a listing must state every one. */
  diet?: string;
  q?: string;
  page?: number;
  limit?: number;
}

// `meta` is a sibling of `data` for list responses (sendSuccess 4-arg form).
interface ListEnvelope<T> {
  success: true;
  data: T[];
  meta: PaginatedResponse<unknown>["meta"];
}

export function listProviders(params?: ListProvidersParams) {
  return api.get<ListEnvelope<ProviderListItem>>(
    "/providers",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function getProviderCategories() {
  return api.get<ApiResponse<ProviderCategory[]>>("/providers/categories");
}

// ─── Become / manage a provider ─────────────────────────────────────
// POST /providers and PUT /providers/:id. Both are auth-gated; PUT is
// owner-gated server-side (you must have added or claimed the listing).
// Mirrors createProviderSchema in providers.schema.ts.

export interface ProviderInput {
  name: string;
  categoryCode: string;
  subcategoryCode?: string;
  description?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  city: string;
  state: string;
  zipCode?: string;
  originState?: string;
  languages?: string[];
  skills?: string[];
  images?: string[];
}

// The create/update service returns a trimmed provider (enough to route
// to its detail page), NOT the full ProviderDetail shape.
export interface SavedProvider {
  id: string;
  name: string;
  slug: string;
  category: { code: string; name: string };
  subcategory: { code: string; name: string } | null;
  city: string;
  state: string;
  originState: string | null;
}

export function createProvider(input: ProviderInput) {
  return api.post<ApiResponse<SavedProvider>>("/providers", input);
}

/** The owner's diet facts on a food business; an empty list takes them off. */
export function setProviderDietFacts(id: string, dietFacts: string[]) {
  return api.put<ApiResponse<SavedProvider>>(`/providers/${id}`, { dietFacts });
}

export function updateProvider(id: string, input: Partial<ProviderInput>) {
  return api.put<ApiResponse<SavedProvider>>(`/providers/${id}`, input);
}

export function autocompleteProviders(q: string) {
  return api.get<ApiResponse<ProviderListItem[]>>("/providers/autocomplete", {
    q,
  });
}

export function getProvider(idOrSlug: string) {
  return api.get<ApiResponse<ProviderDetail>>(`/providers/${idOrSlug}`);
}

export function getProviderVouches(id: string, params?: { page?: number; limit?: number }) {
  return api.get<ListEnvelope<ProviderVouch>>(
    `/providers/${id}/vouches`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

export interface VouchInput {
  rating: number; // 1–5, required by the backend
  comment?: string;
  /** What they did for you: one of their skills or a few words. Null clears it. */
  occasion?: string | null;
}

export function vouchForProvider(id: string, input: VouchInput) {
  return api.post<ApiResponse<ProviderVouch>>(`/providers/${id}/vouch`, input);
}

export function removeVouch(id: string) {
  return api.delete<unknown>(`/providers/${id}/vouch`);
}

// ─── Provider verification (trust loop) ───────────────────────────
// Submit is owner-gated server-side (403 unless you added or claimed
// the provider); the admin review lives in api/admin.ts.

export type ProviderVerificationType =
  | "identity"
  | "business_license"
  | "professional_certification"
  | "insurance"
  | "background_check";

export interface ProviderVerification {
  id: string;
  type: ProviderVerificationType;
  status: string;
  documents?: string[];
  notes?: string | null;
  reviewNotes?: string | null;
  createdAt?: string;
  reviewedAt?: string | null;
  [key: string]: unknown;
}

export function submitProviderVerification(
  id: string,
  input: { type: ProviderVerificationType; documents: string[]; notes?: string },
) {
  return api.post<ApiResponse<ProviderVerification>>(`/providers/${id}/verify`, input);
}

export function getProviderVerifications(id: string) {
  return api.get<ApiResponse<ProviderVerification[]>>(`/providers/${id}/verifications`);
}

// ─── References (owner-managed) ────────────────────────────────────
// Read via the provider detail payload; these mutate. Owner-gated server-side.

export interface ReferenceInput {
  name: string;
  relationship: string;
  note?: string;
}

export function addProviderReference(id: string, input: ReferenceInput) {
  return api.post<ApiResponse<ProviderReferenceItem>>(`/providers/${id}/references`, input);
}

export function removeProviderReference(id: string, refId: string) {
  return api.delete<unknown>(`/providers/${id}/references/${refId}`);
}

// ============================================================================
// The business's own desk, and the two things a member does to a business.
//
// Shapes read from docs/api/openapi.json. The dashboard schema is unusual: it
// carries explicit RENDERING instructions, and they are contracts rather than
// suggestions. They are repeated at each call below so nobody has to go and
// find them.
// ============================================================================

export interface MyProviderRef {
  id: string;
  name: string;
  slug: string;
  isVerified: boolean;
  kind: string;
  city: string;
  state: string;
  acceptingWork: boolean;
  /**
   * `category.kind` is goods / service / both — what the TRADE deals in.
   *
   * Not to be confused with `kind` above, which is individual vs business.
   * Two different questions, same word, and mixing them up means a pandit is
   * shown a grocery interface.
   */
  category: { code: string; name: string; icon: string | null; kind: string };
}

/** BARE ARRAY, like /institutions/mine. */
export function getMyProviders() {
  return api.get<ApiResponse<MyProviderRef[]>>("/providers/mine");
}

export interface ProviderLead {
  threadId: string;
  from: { id: string; name: string; avatarUrl: string | null; city: string | null };
  /** Newest message, trimmed. "Message removed" when redacted. */
  excerpt: string;
  isUnread: boolean;
  lastMessageAt: string;
}

export interface ProviderOwnerDashboard {
  provider: { id: string; name: string; slug: string; isVerified: boolean };
  /**
   * Inbound demand. RENDER FIRST — it is the point of the screen.
   *
   * `items` arrive UNREAD FIRST then newest. Do not re-sort them.
   *
   * When there is nothing, the block STILL RENDERS as "you're all caught up".
   * Hiding it would move the leads somewhere else on a quiet day, and an owner
   * only learns where leads live if they are always in the same place.
   */
  potentialLeads: { unread: number; openThreads: number; items: ProviderLead[] };
  /** What the owner has put out, and what is still blank. */
  keepItAlive: {
    daysSinceLastUpdate: number | null;
    listingLooksStale: boolean;
    incompleteFields: string[];
    updatesPosted: number;
    lastUpdateAt: string | null;
  };
  reach: {
    followers: number;
    followersLast30d: number;
    /** Forward-looking: who the NEXT update reaches, not who saw the last one. */
    notifiableFollowers: number;
    profileViews30d: number;
    /** Members who tried to message the business before it was claimed, once each. */
    membersTriedBeforeClaim: number;
    /**
     * False when no view has ever been recorded, because views are written by
     * clients and no client writes them yet. When false, HIDE the metric —
     * rendering "0 views" reports a lie as a measurement.
     */
    profileViewsTracked: boolean;
  };
  trust: {
    vouches: number;
    vouchesLast30d: number;
    avgRating: number;
    mentionsInAnswers: number;
    verifications: string[];
  };
}

export function getProviderDashboard(id: string) {
  return api.get<ApiResponse<ProviderOwnerDashboard>>(`/providers/${id}/dashboard`);
}

export const PROVIDER_UPDATE_TYPES = ["general", "offer", "availability"] as const;

export interface ProviderUpdateCard {
  id: string;
  /** Open string — the vocabulary grows; render unknowns rather than dropping. */
  type: string;
  body: string;
  imageUrl: string | null;
  /** Null means evergreen. */
  expiresAt: string | null;
  createdAt: string;
}

export function listProviderUpdates(id: string, params?: { page?: number; limit?: number }) {
  return api.get<ApiResponse<{ updates: ProviderUpdateCard[]; meta: { total: number } }>>(
    `/providers/${id}/updates`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function createProviderUpdate(
  id: string,
  input: { body: string; type?: string; imageUrl?: string | null; expiresAt?: string | null },
) {
  return api.post<ApiResponse<ProviderUpdateCard>>(`/providers/${id}/updates`, input);
}

export function deleteProviderUpdate(id: string, updateId: string) {
  return api.delete<ApiResponse<void>>(`/providers/${id}/updates/${updateId}`);
}

export interface ProviderItemCard {
  id: string;
  name: string;
  /** Free text the shop wrote. There is no checkout; never parse it. */
  priceInfo: string | null;
  imageUrl: string | null;
  videoUrl: string | null;
  sortOrder: number;
  /** False is the shop's own draft state — hidden from the public catalogue. */
  isActive: boolean;
  /** The last day it is offered, YYYY-MM-DD; null all year. Past it, only the owner sees it. */
  availableUntil: string | null;
  /** The shop's own statement about this item's food. */
  dietFacts: string[];
}

/**
 * The shop's catalogue — the commerce spine the Bazaar draws stalls from.
 *
 * It hangs off the BUSINESS, not off a stall, so a shop open in March still
 * has somewhere to put its products.
 *
 * `includeInactive` is what an OWNER passes to see their own drafts. Without
 * it the read is the public catalogue, so a hidden item disappears from the
 * owner's screen too and there is no way back to un-hide it — hiding becomes a
 * one-way door. The server gates the flag on real ownership and, for anyone
 * else, quietly returns the public list rather than a 403, since refusing
 * would itself confirm that hidden items exist.
 */
export function listProviderItems(id: string, includeInactive = false) {
  return api.get<ApiResponse<{ items: ProviderItemCard[]; total: number }>>(
    `/providers/${id}/items`,
    includeInactive ? { includeInactive: true } : undefined,
  );
}

export function createProviderItem(
  id: string,
  input: {
    name: string; priceInfo?: string; imageUrl?: string; sortOrder?: number; availableUntil?: string; dietFacts?: string[];
  },
) {
  return api.post<ApiResponse<ProviderItemCard>>(`/providers/${id}/items`, input);
}

export function updateProviderItem(
  id: string,
  itemId: string,
  input: Partial<{
    name: string; priceInfo: string | null; imageUrl: string | null; sortOrder: number; isActive: boolean;
    availableUntil: string | null; dietFacts: string[];
  }>,
) {
  return api.patch<ApiResponse<ProviderItemCard>>(`/providers/${id}/items/${itemId}`, input);
}

export function deleteProviderItem(id: string, itemId: string) {
  return api.delete<ApiResponse<void>>(`/providers/${id}/items/${itemId}`);
}

export function followProvider(id: string) {
  return api.post<ApiResponse<{ following: boolean }>>(`/providers/${id}/follow`, {});
}

export function unfollowProvider(id: string) {
  return api.delete<ApiResponse<{ following: boolean }>>(`/providers/${id}/follow`);
}

/**
 * Opens (or continues) a thread with the business. This is a LEAD. One
 * `idempotencyKey` per message; a retry with the same key returns the same message.
 */
export function sendProviderMessage(id: string, body: string, idempotencyKey?: string) {
  return api.post<ApiResponse<{ threadId: string; message: unknown }>>(
    `/providers/${id}/messages`,
    { body, ...(idempotencyKey ? { idempotencyKey } : {}) },
  );
}

export interface ProviderAsksCategory {
  code?: string;
  name?: string;
  asks?: unknown[];
  unansweredCount?: number;
}

/**
 * Questions waiting on a business, grouped by trade.
 *
 * `totalUnanswered` is the recruitment number: a question nobody could answer
 * is a trade with nobody in it.
 */
export function getProviderAsks(params?: { city?: string; limit?: number }) {
  return api.get<
    ApiResponse<{
      categories: ProviderAsksCategory[];
      meta: { totalAsks: number; totalUnanswered: number };
    }>
  >("/providers/asks", params as Record<string, string | number | boolean | undefined>);
}
